import { BlockMath, InlineMath } from "react-katex";

/* Unicode superscript / subscript digit maps */
const SUPS = { "0":"⁰","1":"¹","2":"²","3":"³","4":"⁴","5":"⁵","6":"⁶","7":"⁷","8":"⁸","9":"⁹",
               "+":"⁺","-":"⁻","=":"⁼","(":"⁽",")":"⁾","n":"ⁿ","i":"ⁱ","x":"ˣ","y":"ʸ" };
const SUBS = { "0":"₀","1":"₁","2":"₂","3":"₃","4":"₄","5":"₅","6":"₆","7":"₇","8":"₈","9":"₉",
               "+":"₊","-":"₋","=":"₌","(":"₍",")":"₎","a":"ₐ","e":"ₑ","i":"ᵢ","o":"ₒ","x":"ₓ","n":"ₙ" };

function mapSup(s) { return s.split("").map(ch => SUPS[ch] || ch).join(""); }
function mapSub(s) { return s.split("").map(ch => SUBS[ch] || ch).join(""); }

/**
 * Rewrite plain-text math like `5^2`, `x^{n}`, `H_2O` into pretty Unicode forms.
 * - Skip rewrites within LaTeX delimiters ($...$, $$...$$) — those are rendered by KaTeX.
 * - Preserves the original string when characters can't be mapped.
 */
export function prettyMath(input) {
  if (typeof input !== "string") return input;
  // Don't transform stuff inside $...$ delimiters (KaTeX handles those).
  // Strategy: split on the inline-math token regex, transform only the gaps.
  const tokenRe = /(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$)/g;
  const parts = input.split(tokenRe);
  return parts.map((seg) => {
    if (!seg) return seg;
    if (seg.startsWith("$")) return seg; // leave LaTeX alone
    let s = seg;
    // x^{abc} → use unicode when EVERY char of the exponent has a superscript
    s = s.replace(/\^\{([^}]+)\}/g, (m, g) => {
      const allMapped = g.split("").every((c) => c in SUPS);
      return allMapped ? mapSup(g) : `^{${g}}`;
    });
    s = s.replace(/\^\(([^)]+)\)/g, (m, g) => {
      // Only convert parenthesised exponents when EVERY char is mappable;
      // otherwise leave the original ^(...) for LaTeX/KaTeX to handle.
      const mapped = mapSup(g);
      const allMapped = g.split("").every((c) => c in SUPS);
      return allMapped ? mapped : `^(${g})`;
    });
    // x^N (single token: digit run or single letter)
    s = s.replace(/\^([0-9]+)/g, (_, g) => mapSup(g));
    s = s.replace(/\^([+\-])([0-9]+)/g, (_, sign, digs) => mapSup(sign + digs));
    s = s.replace(/\^([a-z])\b/gi, (_, g) => SUPS[g] || `^${g}`);

    // Subscripts H_2O, x_1, x_{n+1}
    s = s.replace(/_\{([^}]+)\}/g, (m, g) => {
      const allMapped = g.split("").every((c) => c in SUBS);
      return allMapped ? mapSub(g) : `_{${g}}`;
    });
    s = s.replace(/_([0-9]+)/g, (_, g) => mapSub(g));
    s = s.replace(/_([a-z])\b/gi, (_, g) => SUBS[g] || `_${g}`);

    // Common ASCII tokens → unicode
    s = s.replace(/\bpi\b/g, "π")
         .replace(/\btheta\b/g, "θ")
         .replace(/\balpha\b/g, "α")
         .replace(/\bbeta\b/g, "β")
         .replace(/\bgamma\b/g, "γ")
         .replace(/\bdelta\b/g, "δ")
         .replace(/\bsigma\b/g, "σ")
         .replace(/\binfty\b/g, "∞")
         .replace(/\+\/-/g, "±")
         .replace(/<=|=</g, "≤")
         .replace(/>=|=>/g, "≥")
         .replace(/!=|<>/g, "≠")
         .replace(/->/g, "→")
         .replace(/\bsqrt\(([^)]+)\)/g, "√($1)")
         .replace(/\* /g, "× ");

    return s;
  }).join("");
}

/**
 * Renders text that may contain LaTeX. Splits on $...$ (inline) and $$...$$ (block).
 * Plain-text math like `5^2` or `H_2O` is pre-processed to nicer Unicode equivalents.
 */
export default function MathText({ children, block = false, className = "" }) {
  // Defensive coercion: occasionally a question payload arrives as an object
  // (e.g. {text, latex} from some generator or a raw API payload). Without
  // this guard React would render "[object Object]" which is what users were
  // seeing. We try common text-bearing keys before falling back to JSON.
  let raw;
  if (typeof children === "string") {
    raw = children;
  } else if (children == null) {
    return null;
  } else if (typeof children === "number" || typeof children === "boolean") {
    raw = String(children);
  } else if (Array.isArray(children)) {
    raw = children.map((c) => (typeof c === "string" ? c : (c?.text || c?.question || c?.value || ""))).join("");
  } else if (typeof children === "object") {
    raw = children.text ?? children.question ?? children.stem ?? children.value ?? children.latex ?? "";
    if (!raw && children) {
      try { raw = JSON.stringify(children); } catch { raw = ""; }
    }
    raw = String(raw || "");
  } else {
    raw = String(children ?? "");
  }
  if (!raw) return null;
  const text = prettyMath(raw);

  // explicit block-only render
  if (block && !text.includes("$")) {
    return (
      <span className={className}>
        <BlockMath math={text} errorColor="#F43F5E"/>
      </span>
    );
  }

  // Tokenize: $$...$$ → block, $...$ → inline, rest → plain text
  const parts = [];
  const regex = /(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$)/g;
  let last = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    const tok = m[0];
    if (tok.startsWith("$$")) {
      parts.push({ type: "block", value: tok.slice(2, -2).trim() });
    } else {
      parts.push({ type: "inline", value: tok.slice(1, -1).trim() });
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });

  // If no $ markers were present, try to render the whole thing as inline math if it
  // looks like a LaTeX expression (contains backslash commands or known operators).
  if (parts.length === 1 && parts[0].type === "text") {
    const looksLikeLatex = /\\(frac|sqrt|sum|int|leq|geq|neq|times|cdot|theta|alpha|beta|gamma|delta|pi|infty|rightarrow|left|right)\b/.test(text)
      || /[\^_]\{/.test(text);
    if (looksLikeLatex) {
      return (
        <span className={className}>
          <InlineMath math={text} errorColor="#F43F5E"/>
        </span>
      );
    }
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (p.type === "block") return <BlockMath key={i} math={p.value} errorColor="#F43F5E"/>;
        if (p.type === "inline") return <InlineMath key={i} math={p.value} errorColor="#F43F5E"/>;
        return <span key={i}>{p.value}</span>;
      })}
    </span>
  );
}
